import { useAppStore } from "@/stores";
import useSWR from "swr";
import { getAll, getDeals, getDealstageAvgTimesByCompanyId } from "@/lib/supabase";
import { Loader } from "@/components/Loader";
// import { LoadingError } from "@/components/LoadingError";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { useState } from "react";
import { getDealOwner } from "@revelate/utils";
import { getAPI } from "@/lib/api";
import { Select, SelectTrigger, SelectContent, SelectValue, SelectItem } from "@/components/ui/select";
import {
  evaluateRiskPipelineCoverage,
  formatCurrencyValue,
  prepareDealsForRiskAssessment,
  prepareDealsWithCurrencyConversion,
} from "../lib/risk";
import { RiskHighlightBadge, RiskHighlightBadges, RiskHighlightCard } from "../components/RiskHighlightCard";
import { getCompanyTarget } from "../lib/companies";
import { SectionCard } from "../components/SectionCard";
import { PipelineCoverageChart } from "../components/charts";
import { AIInsightCard } from "../components/AIInsightCard";
import { endOfQuarter, startOfQuarter } from "date-fns";
import { usePostHog } from "@posthog/react";
import { usePageTimeTracking } from "@/lib/posthog/usePageTimeTracking";

// Important dates
const now = new Date();
const startOfThisQuarter = startOfQuarter(now);
const endOfThisQuarter = endOfQuarter(now);

export function RevenueIntelligencePipelineCoverage() {
  const [globalSelectedPipeline, setGlobalSelectedPipeline] = useState<string>("all");
  const [globalSelectedOwner, setGlobalSelectedOwner] = useState<string>("all");

  const { currentUser, companyCurrency } = useAppStore((state) => state);

  const posthog = usePostHog();
  usePageTimeTracking("l2_pipeline_coverage", posthog);

  const {
    data: allOpenDeals,
    // error: allOpenDealsError,
    // isLoading: isLoadingAllOpenDeals,
  } = useSWR(
    [
      'query:allOpenDeals',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getDeals(companyId, {
      onlyOpen: true,
      nonZeroValue: true,
      // includeEmails: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: wonDealsThisQuarter,
    // error: wonDealsThisQuarterError,
    // isLoading: isLoadingWonDealsThisQuarter,
  } = useSWR(
    [
      'query:wonDealsThisQuarter',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getDeals(companyId, {
      dateRange: {
        from: startOfThisQuarter.toISOString(),
        to: endOfThisQuarter.toISOString(),
      },
      onlyWon: true,
      nonZeroValue: true,
      includeEmails: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: currencies,
    // error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useSWR(
    [
      'query:currencies',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAll("currencies", companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const {
    data: pipelines,
    // error: pipelinesError,
    // isLoading: isLoadingPipelines,
  } = useSWR(
    [
      'query:pipelines',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAPI(`providers/hubspot/pipelines/${companyId}`),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const pipelinesForSelect = pipelines && pipelines.results
    ? [
      {
        value: "all",
        label: "All Pipelines",
      },
      ...pipelines.results
        .map((p: any) => ({
          value: p.id,
          label: p.label,
        })),
    ]
    : []

  const {
    data: dealstageAvgTimes,
    // error: dealstageAvgTimesError,
    // isLoading: isLoadingdealstageAvgTimes,
  } = useSWR(
    [
      'query:dealstageAvgTimes',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => companyId
      ? getDealstageAvgTimesByCompanyId(parseInt(companyId, 10))
      : [],
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  let ownersForSelect = [{
    value: "all",
    label: "All Sales Reps",
  }];

  if (allOpenDeals) {
    const openDealOwners = allOpenDeals.map(d => getDealOwner(d));

    const uniqueOwnersMap = new Map();
    openDealOwners.forEach(owner => {
      if (owner?.id && !uniqueOwnersMap.has(owner.id)) {
        uniqueOwnersMap.set(owner.id, owner);
      }
    });
    const uniqueOwnersArray = Array.from(uniqueOwnersMap.values());

    ownersForSelect = [
      ...ownersForSelect,
      ...uniqueOwnersArray.map(owner => ({
        value: owner.id,
        label: `${owner.first_name} ${owner.last_name}`,
      }))
    ];
  }

  if (
    // isLoadingAllOpenDeals ||
    // !allOpenDeals
    isLoadingCurrencies
  ) return <Loader />;

  // if (
  //   allOpenDealsError
  // ) return <LoadingError />;

  const hubspotDealsForPipelineCoverage = prepareDealsForRiskAssessment(
    allOpenDeals,
    pipelines,
    // @ts-ignore
    currencies,
    currentUser?.company,
    dealstageAvgTimes,
  );

  const wonDealsThisQuarterForPipelineCoverage = prepareDealsWithCurrencyConversion(
    wonDealsThisQuarter,
    // @ts-ignore
    currencies,
    currentUser?.company,
  );

  const companyTarget = getCompanyTarget(currentUser?.company_id ?? 0);

  const {
    summary: pipelineCoverageSummary,
    insight: pipelineCoverageInsight,
    coverage,
    // coverageChangePct,
    // coverageChangeIsGood,
    isEarlyStageWeak: coverageIsEarlyStageWeak,
    isMidStageWeak: coverageIsMidStageWeak,
    isLateStageWeak: coverageIsLateStageWeak,
    totalDealValueExpectedToCloseThisMonth,
    totalDealValueExpectedToCloseThisQuarter,
    chartData: chartDataPipelineCoverage,
  } = evaluateRiskPipelineCoverage(
    hubspotDealsForPipelineCoverage ?? [],
    currentUser?.company_id,
    wonDealsThisQuarterForPipelineCoverage ?? [],
    pipelines?.results ?? [],
    companyTarget,
    globalSelectedPipeline,
    globalSelectedOwner,
  );

  return (
    <div className="space-y-4">
      <Card className="border-none bg-transparent">
        <CardContent className="px-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row justify-between items-center">
              <h1>Pipeline Coverage</h1>
              {/* Global filters */}
              <div className="flex flex-row justify-end items-center gap-x-3">
                <p className="text-sm text-gray-500">Filters</p>
                <div className="flex flex-row gap-2">
                  <div className="flex flex-row gap-2 items-center">
                    <Select
                      value={globalSelectedPipeline}
                      onValueChange={v => setGlobalSelectedPipeline(v)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelinesForSelect.map((p: any) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-row gap-2 items-center">
                    <Select
                      value={globalSelectedOwner}
                      onValueChange={v => setGlobalSelectedOwner(v)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ownersForSelect.map((owner: any) => (
                          <SelectItem key={owner.value} value={owner.value}>
                            {owner.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-x-2 gap-y-4 w-full pb-8">
              <RiskHighlightCard
                title="Pipeline coverage"
                headline={`${(coverage / 100).toFixed(1)}x`}
                //change={coverageChangePct}
                //isChangeGood={coverageChangeIsGood}
                //changeDescription="vs 30d ago"
                text={pipelineCoverageSummary}
                body={
                  <RiskHighlightBadges>
                    <RiskHighlightBadge
                      text="Early stage"
                      dotColor={coverageIsEarlyStageWeak ? "red" : "green"}
                    />
                    <RiskHighlightBadge
                      text="Mid stage"
                      dotColor={coverageIsMidStageWeak ? "red" : "green"}
                    />
                    <RiskHighlightBadge
                      text="Late stage"
                      dotColor={coverageIsLateStageWeak ? "red" : "green"}
                    />
                  </RiskHighlightBadges>
                }
              />
              <RiskHighlightCard
                title="Expected to close this month"
                headline={`${formatCurrencyValue(totalDealValueExpectedToCloseThisMonth, companyCurrency, 1)}`}
              //change={0}
              //isChangeGood={true}
              //changeDescription="vs 30d ago"
              />
              <RiskHighlightCard
                title="Expected to close this quarter"
                headline={`${formatCurrencyValue(totalDealValueExpectedToCloseThisQuarter, companyCurrency, 1)}`}
              //change={0}
              //isChangeGood={true}
              //changeDescription="vs 30d ago"
              />

              <SectionCard className="col-span-1">
                <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                  <div className="w-full flex flex-row justify-between items-center pb-4">
                    {/* Header */}
                    <div>
                      <p className="font-medium text-sm">
                        Pipeline coverage
                      </p>
                      <p className="text-xs text-gray-500">
                        Coverage ratio vs quota
                      </p>
                    </div>
                  </div>

                  {/* Pipeline coverage chart */}
                  <div>
                    <PipelineCoverageChart
                      data={chartDataPipelineCoverage}
                    />
                    <AIInsightCard
                      text={pipelineCoverageInsight}
                    />
                  </div>
                </div>
              </SectionCard>

              <SectionCard className="col-span-2 flex-1">
                <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                  <div className="w-full flex flex-row justify-between items-center pb-4">
                    {/* Header */}
                    <div>
                      <p className="font-medium text-sm">
                        Coverage trend
                      </p>
                      <p className="text-xs text-gray-500">
                        Pipeline coverage by month
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-100 h-[406px] flex justify-center items-center bg-gradient-to-br from-[#9F86C0]/15 to-gray-50 rounded-[10px]">
                    <p className="text-gray-400 font-display">
                      Coming soon
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  );
}
